import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth, collection, db, doc, getDoc, getDocs, query, serverTimestamp, setDoc, where } from '../../firebase/firebase';
import ImageUploader from '../../components/ImageKit/ImageUploader';
import { useUser } from '../../firebase/UserProvider';
import './ProfileSetupPage.css';

const cleanUsername = (value) => value.toLowerCase().replace(/[^a-z0-9_]/g, '').slice(0, 24);

const ProfileSetupForm = () => {
  const [userData, setUserData] = useState({
    fullName: '',
    username: '',
    email: '',
    profilePhoto: '',
    businessName: '',
    businessAddress: '',
    village: '',
    district: '',
    state: '',
    bio: '',
    isProfileCompleted: false,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [usernameAvailable, setUsernameAvailable] = useState(null);
  const [usernameChecking, setUsernameChecking] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const navigate = useNavigate();
  const { refreshUser } = useUser();

  const requiredFieldsComplete = useMemo(() => (
    userData.fullName.trim() &&
    userData.username.trim() &&
    userData.businessName.trim() &&
    userData.district.trim() &&
    userData.state.trim()
  ), [userData]);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      if (!user) {
        navigate('/auth');
        return;
      }

      const userDocRef = doc(db, 'users', user.uid);
      const userDoc = await getDoc(userDocRef);
      const existingData = userDoc.exists() ? userDoc.data() : {};

      setUserData((current) => ({
        ...current,
        ...existingData,
        email: user.email || existingData.email || '',
        fullName: existingData.fullName || existingData.name || existingData.displayName || user.displayName || '',
        profilePhoto: existingData.profilePhoto || existingData.photoURL || user.photoURL || '',
      }));
      setUsernameAvailable(existingData.username ? true : null);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [navigate]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    const nextValue = name === 'username' ? cleanUsername(value) : value;

    setUserData((current) => ({
      ...current,
      [name]: nextValue,
    }));

    if (name === 'username') {
      setUsernameAvailable(null);
    }
  };

  const checkUsernameAvailability = async () => {
    const username = userData.username.trim();
    if (!username) {
      setUsernameAvailable(null);
      return false;
    }

    if (username.length < 4) {
      setUsernameAvailable(false);
      setError('Username must be at least 4 characters.');
      return false;
    }

    setUsernameChecking(true);
    setError('');

    try {
      const usersRef = collection(db, 'users');
      const usernameQuery = query(usersRef, where('username', '==', username));
      const snapshot = await getDocs(usernameQuery);
      const currentUser = auth.currentUser;
      const belongsToCurrentUser = snapshot.docs.length === 1 && snapshot.docs[0].id === currentUser?.uid;

      setUsernameAvailable(snapshot.empty || belongsToCurrentUser);
      if (!snapshot.empty && !belongsToCurrentUser) {
        setError('Username is already taken.');
      }
      return snapshot.empty || belongsToCurrentUser;
    } catch (err) {
      setError('Could not check username right now.');
      console.error(err);
      return false;
    } finally {
      setUsernameChecking(false);
    }
  };

  const handleImageUpload = (url) => {
    setUserData((current) => ({
      ...current,
      profilePhoto: url,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setSuccess('');

    if (!requiredFieldsComplete) {
      setError('Please complete the required fields.');
      return;
    }

    if (usernameAvailable !== true) {
      const isAvailable = await checkUsernameAvailability();
      if (!isAvailable) return;
    }

    try {
      setSaving(true);
      const user = auth.currentUser;
      if (!user) {
        navigate('/auth');
        return;
      }

      const location = [userData.village, userData.district, userData.state]
        .filter(Boolean)
        .join(', ');

      const payload = {
        fullName: userData.fullName.trim(),
        name: userData.fullName.trim(),
        displayName: userData.fullName.trim(),
        username: userData.username.trim(),
        email: user.email,
        profilePhoto: userData.profilePhoto || '',
        photoURL: userData.profilePhoto || '',
        businessName: userData.businessName.trim(),
        businessAddress: userData.businessAddress.trim(),
        village: userData.village.trim(),
        district: userData.district.trim(),
        state: userData.state.trim(),
        location,
        bio: userData.bio.trim(),
        isProfileCompleted: true,
        updatedAt: serverTimestamp(),
      };

      if (!userData.createdAt) {
        payload.createdAt = serverTimestamp();
      }

      await setDoc(doc(db, 'users', user.uid), payload, { merge: true });
      await refreshUser?.();
      setSuccess('Profile saved. Welcome to Ankurit.');
      setTimeout(() => navigate('/profile'), 700);
    } catch (err) {
      setError(`Error saving profile: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="loading-container">Loading profile...</div>;
  }

  return (
    <main className="setup-container">
      <section className="form-card">
        <div className="setup-intro">
          <span>Profile</span>
          <h1>{userData.isProfileCompleted ? 'Edit your profile' : 'Create your profile'}</h1>
          <p>Keep it short and useful. Buyers only need enough context to trust your listings and message you inside Ankurit.</p>
        </div>

        {error && <div className="alert-message error">{error}</div>}
        {success && <div className="alert-message success">{success}</div>}

        <form onSubmit={handleSubmit} className="form-content">
          <div className="profile-photo-row">
            <ImageUploader
              userId={auth.currentUser?.uid}
              type="profile"
              currentImageUrl={userData.profilePhoto}
              onUploadComplete={handleImageUpload}
              label="Profile photo"
            />
          </div>

          <div className="input-grid">
            <div className="input-group">
              <label htmlFor="fullName">Full name *</label>
              <input id="fullName" name="fullName" value={userData.fullName} onChange={handleChange} className="input-field" required />
            </div>

            <div className="input-group">
              <label htmlFor="username">Username *</label>
              <div className="input-with-feedback">
                <input id="username" name="username" value={userData.username} onChange={handleChange} onBlur={checkUsernameAvailability} className="input-field" required />
                {usernameChecking && <span className="input-status checking">Checking</span>}
                {usernameAvailable === true && <span className="input-status available">Available</span>}
                {usernameAvailable === false && <span className="input-status unavailable">Taken</span>}
              </div>
            </div>

            <div className="input-group">
              <label htmlFor="email">Email</label>
              <input id="email" type="email" name="email" value={userData.email} className="input-field" readOnly />
            </div>

            <div className="input-group">
              <label htmlFor="businessName">Nursery / farm name *</label>
              <input id="businessName" name="businessName" value={userData.businessName} onChange={handleChange} className="input-field" required />
            </div>

            <div className="input-group">
              <label htmlFor="village">Village / area</label>
              <input id="village" name="village" value={userData.village} onChange={handleChange} className="input-field" />
            </div>

            <div className="input-group">
              <label htmlFor="district">District *</label>
              <input id="district" name="district" value={userData.district} onChange={handleChange} className="input-field" required />
            </div>

            <div className="input-group">
              <label htmlFor="state">State *</label>
              <input id="state" name="state" value={userData.state} onChange={handleChange} className="input-field" required />
            </div>
          </div>

          <div className="input-group">
            <label htmlFor="businessAddress">Address shown to buyers</label>
            <textarea id="businessAddress" name="businessAddress" value={userData.businessAddress} onChange={handleChange} className="input-field" rows="3" />
          </div>

          <div className="input-group">
            <label htmlFor="bio">Short bio</label>
            <textarea id="bio" name="bio" value={userData.bio} onChange={handleChange} className="input-field" rows="3" maxLength={280} />
          </div>

          <div className="form-actions">
            <button type="button" className="action-btn secondary" onClick={() => navigate('/profile')}>Cancel</button>
            <button type="submit" className="action-btn primary" disabled={saving || !requiredFieldsComplete}>
              {saving ? 'Saving...' : 'Save profile'}
            </button>
          </div>
        </form>
      </section>
    </main>
  );
};

export default ProfileSetupForm;
